'use client';

import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { BackendAuth } from '@/lib/auth-backend';
import { useTranslation } from '@/hooks/useTranslation';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const STATUS_VARIANT = {
  received: 'secondary',
  in_progress: 'warning',
  question: 'destructive',
  done: 'success',
};

const STATUS_FALLBACK = {
  received: 'Eingegangen',
  in_progress: 'In Bearbeitung',
  question: 'Rückfrage an dich',
  done: 'Erledigt',
};

function fmt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

function StatusBadge({ status }) {
  const { t } = useTranslation();
  return (
    <Badge variant={STATUS_VARIANT[status] || 'secondary'} appearance="light" size="sm">
      {t(`bugReport.status_${status}`, {
        defaultValue: STATUS_FALLBACK[status] || status,
        editable: false,
      })}
    </Badge>
  );
}

/** Zaehler fuer das Header-Badge. Fragt alle 60 s und beim Zurueckkehren zum Tab. */
export function useBugReportUnread() {
  const [count, setCount] = useState(0);
  const refresh = useCallback(async () => {
    try {
      const r = await BackendAuth.apiCallJson('/api/bug-report/mine/unread-count');
      setCount(Number(r?.count) || 0);
    } catch {
      /* Badge ist Komfort - ein Fehler darf nie stoeren */
    }
  }, []);

  useEffect(() => {
    refresh();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, 60000);
    const onVis = () => document.visibilityState === 'visible' && refresh();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [refresh]);

  return { count, refresh };
}

export function MyBugReports({ onChanged }) {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await BackendAuth.apiCallJson('/api/bug-report/mine');
      setItems(r?.data || []);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const open = async (id) => {
    setLoadingDetail(true);
    try {
      const r = await BackendAuth.apiCallJson(`/api/bug-report/mine/${id}`);
      setDetail(r?.data || null);
      onChanged?.();
    } finally {
      setLoadingDetail(false);
    }
  };

  const sendReply = async () => {
    const text = reply.trim();
    if (text.length < 2 || sending || !detail) return;
    setSending(true);
    try {
      await BackendAuth.apiCallJson(`/api/bug-report/mine/${detail.id}/reply`, {
        method: 'POST',
        body: JSON.stringify({ text }),
      });
      setReply('');
      toast.success(t('bugReport.reply_sent', { defaultValue: 'Antwort gesendet', editable: false }));
      await open(detail.id);
    } catch (err) {
      toast.error(err?.message || t('bugReport.error', { editable: false }));
    } finally {
      setSending(false);
    }
  };

  const back = () => {
    setReply('');
    setDetail(null);
    load();
  };

  if (items === null) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (detail) {
    return (
      <div className="space-y-4">
        <Button type="button" variant="ghost" size="sm" onClick={back}>
          <ArrowLeft className="size-4" />
          {t('bugReport.mine_back', { defaultValue: 'Zurück', editable: false })}
        </Button>
        <div className="space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground">
              {t('bugReport.mine_number', { defaultValue: 'Meldung', editable: false })} #{detail.id}
            </span>
            <StatusBadge status={detail.status} />
          </div>
          <h3 className="text-base font-semibold break-words">{detail.title}</h3>
          <div className="text-xs text-muted-foreground">{fmt(detail.created_at)}</div>
        </div>
        <p className="text-sm whitespace-pre-wrap break-words rounded-md border border-border bg-muted/40 p-3">
          {detail.description}
        </p>
        {detail.screenshot && (
          <img
            src={detail.screenshot}
            alt="screenshot"
            className="max-h-48 w-auto rounded-md border border-border"
          />
        )}
        <div className="space-y-2">
          {detail.comments.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {t('bugReport.mine_no_replies', {
                defaultValue: 'Noch keine Antwort vom Support.',
                editable: false,
              })}
            </p>
          )}
          {detail.comments.map((c) => (
            <div
              key={c.id}
              className={`rounded-md border p-3 ${
                c.mine ? 'border-border bg-muted/40 ms-6' : 'border-primary/30 bg-primary/5'
              }`}
            >
              <div className="text-xs text-muted-foreground mb-1">
                {c.mine
                  ? t('bugReport.mine_you', { defaultValue: 'Du', editable: false })
                  : t('bugReport.mine_support', { defaultValue: 'Support', editable: false })}{' '}
                · {fmt(c.created_at)}
              </div>
              <div className="text-sm whitespace-pre-wrap break-words">{c.text}</div>
            </div>
          ))}
        </div>
        {detail.can_reply && (
          <div className="space-y-2">
            {detail.status === 'question' && (
              <p className="text-sm font-medium text-destructive">
                {t('bugReport.reply_question_hint', {
                  defaultValue: 'Der Support hat eine Rückfrage an dich – bitte antworte hier.',
                  editable: false,
                })}
              </p>
            )}
            <Textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              maxLength={4000}
              rows={3}
              placeholder={t('bugReport.reply_placeholder', {
                defaultValue: 'Antwort oder weitere Infos schreiben …',
                editable: false,
              })}
            />
            <div className="flex justify-end">
              <Button type="button" size="sm" onClick={sendReply} disabled={sending || reply.trim().length < 2}>
                {sending && <Loader2 className="size-4 animate-spin" />}
                {t('bugReport.reply_send', { defaultValue: 'Senden', editable: false })}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        {t('bugReport.mine_empty', { defaultValue: 'Du hast noch keine Meldung gesendet.', editable: false })}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {loadingDetail && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
      {items.map((it) => (
        <button
          key={it.id}
          type="button"
          onClick={() => open(it.id)}
          className="w-full text-left rounded-md border border-border hover:bg-accent px-3 py-2 flex items-center gap-3"
        >
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{it.title}</div>
            <div className="text-xs text-muted-foreground">
              #{it.id} · {fmt(it.created_at)}
            </div>
          </div>
          {it.has_news && (
            <Badge variant="primary" size="sm">
              {t('bugReport.mine_news', { defaultValue: 'Neu', editable: false })}
            </Badge>
          )}
          <StatusBadge status={it.status} />
        </button>
      ))}
    </div>
  );
}
